import { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import type { ReCAPTCHA, ReCAPTCHAProps } from 'react-google-recaptcha'
import { reCaptchaSiteKey } from '../config'

export interface UseReCaptcha {
  captchaToken: string | null
  captchaError: string | null
  validateCaptcha: () => string | null
  reCaptchaProps: ReCAPTCHAProps & { ref: RefObject<ReCAPTCHA> }
}

export const useReCaptcha = (): UseReCaptcha => {
  const reCaptcha = useRef<ReCAPTCHA>(null)

  const [captchaError, setCaptchaError] = useState<string | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)

  const onChangeCaptcha = useCallback<NonNullable<ReCAPTCHAProps['onChange']>>((token) => {
    setCaptchaToken(token ?? null)
    setCaptchaError((currentError) => (token ? null : currentError))
  }, [])

  const validateCaptcha = useCallback(() => {
    setCaptchaError(captchaToken ? null : 'Please complete the captcha')
    return captchaToken
  }, [captchaToken])

  // using `useMemo` instead of `useEffect` to ensure it is run before the initial
  // render, but not in subsequent ones
  useMemo(() => {
    if (typeof window === 'undefined') {
      return
    }
    window.recaptchaOptions = {
      ...window.recaptchaOptions,
      useRecaptchaNet: true,
    }
  }, [])

  return {
    captchaToken,
    captchaError,
    validateCaptcha,
    reCaptchaProps: { ref: reCaptcha, sitekey: reCaptchaSiteKey, onChange: onChangeCaptcha },
  }
}
